// SkinFrame — the Tier B atmosphere wrap for per-slide theme-world skins
// (KULA-CREATE-LIBRARY-PLAN.md Phase 4, behind `slide_skins`).
//
// A skin changes the dress and nothing else, and it activates ONLY off the
// slide's own `themeOverride` — the field the flagged picker writes. A course
// that is merely THEMED as a world (theme worlds are selectable course-wide
// today, flag or no flag) must render byte-identically to before Phase 4, so
// the resolved theme alone is never an activation signal. Three gates:
//   1. `slide.themeOverride` names a theme world (the author opted this slide in);
//   2. that override actually won theme resolution (theme.id matches — brand
//      and scenario-group overrides take precedence in getSlideTheme);
//   3. the world may honestly dress this slide (canWorldDressSlide — the same
//      grammar guard the picker enforces, so a stale or hand-edited override
//      can't put arcade win/lose chrome on a no-right-answer slide).
// Otherwise children render untouched.
//
// The chrome is CSS-only and purely presentational: no mechanics, no animation
// (static by design, so prefers-reduced-motion needs no special casing), and it
// must NEVER alter the children's interactive semantics.

import React from 'react';
import type { SlideData, Theme, ThemeId } from '../slideTypes';
import { themes } from '../slideTypes';
import {
  isThemeWorld,
  type ThemeWorldId,
} from '../../../../lib/scenario-builder/composition/themeWorlds';
import { canWorldDressSlide } from '../../../../lib/slideSkins';

interface WorldChrome {
  /** Backdrop treatment on the outer wrap. */
  ground: React.CSSProperties;
  /** Treatment on the inner frame that holds the template's render. */
  frame: React.CSSProperties;
  /** World-voiced header chip. */
  chip: { label: string; style: React.CSSProperties };
  /** Optional full-bleed overlay (e.g. neon-oracle scanlines). */
  overlay?: React.CSSProperties;
}

// Per-world chrome v1. Colours lean on the world's own theme palette
// (themes[world].colors) with a few bespoke chrome values — same licence the
// flagship renderers take. Kept deliberately quiet: the template underneath
// still paints its own themed surface.
const worldChrome = (world: ThemeWorldId): WorldChrome => {
  const c = themes[world].colors;
  const fonts = themes[world].fonts;
  switch (world) {
    case 'hyperpop':
      // Loud ground, thick black frame, hard shadow, mono uppercase chip.
      return {
        ground: { background: c.accent },
        frame: {
          border: `4px solid ${c.text}`,
          boxShadow: `8px 8px 0 ${c.text}`,
          background: c.background,
        },
        chip: {
          label: 'ROUND 01',
          style: {
            fontFamily: '"Space Mono", monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            fontWeight: 700,
            color: c.accent,
            background: c.text,
            border: `2px solid ${c.text}`,
          },
        },
      };
    case 'obsidian':
      // Near-black dossier ground, "EXHIBIT" mono chip with a red pin accent.
      return {
        ground: { background: c.background },
        frame: {
          border: '1px solid rgba(184, 146, 74, 0.35)',
          background: c.surface,
        },
        chip: {
          label: 'EXHIBIT',
          style: {
            fontFamily: '"Courier Prime", monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: c.accent,
            border: '1px solid rgba(184, 146, 74, 0.4)',
          },
        },
      };
    case 'aurora':
      // Soft gradient glass ground, rounded translucent card frame.
      return {
        ground: {
          background:
            `radial-gradient(60% 50% at 20% 0%, rgba(94, 242, 160, 0.14), transparent 70%), ` +
            `radial-gradient(50% 60% at 85% 100%, rgba(139, 108, 255, 0.18), transparent 70%), ` +
            `linear-gradient(160deg, ${c.background}, ${c.surface})`,
        },
        frame: {
          borderRadius: 18,
          border: '1px solid rgba(234, 244, 255, 0.18)',
          background: 'rgba(20, 30, 60, 0.55)',
          backdropFilter: 'blur(12px)',
          overflow: 'hidden',
        },
        chip: {
          label: 'Drift point',
          style: {
            fontFamily: '"Hanken Grotesk", system-ui, sans-serif',
            color: c.text,
            background: 'rgba(234, 244, 255, 0.08)',
            border: '1px solid rgba(234, 244, 255, 0.18)',
            borderRadius: 999,
          },
        },
      };
    case 'neon-oracle':
      // Dark terminal ground, CSS scanline overlay, phosphor-green mono chip.
      // Tints derive from the theme's own primary rather than a hardcoded copy
      // of it, so the world stays one palette wherever it's drawn.
      return {
        ground: { background: c.background },
        frame: {
          border: `1px solid ${c.primary}40`,
          background: c.surface,
        },
        chip: {
          label: 'ORACLE FEED',
          style: {
            fontFamily: fonts.code,
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            color: c.primary,
            border: `1px solid ${c.primary}66`,
          },
        },
        overlay: {
          background: `repeating-linear-gradient(0deg, ${c.primary}0d 0px, ${c.primary}0d 1px, transparent 1px, transparent 3px)`,
        },
      };
    case 'terra':
      // Warm botanical ground, leaf-green rule.
      return {
        ground: { background: c.background },
        frame: {
          border: '1px solid rgba(47, 110, 59, 0.25)',
          borderTop: `3px solid ${c.primary}`,
          background: c.surface,
        },
        chip: {
          label: 'Field note',
          style: {
            fontFamily: '"Hanken Grotesk", system-ui, sans-serif',
            color: c.primary,
            borderBottom: `2px solid ${c.primary}`,
          },
        },
      };
    case 'risograph':
      // Paper ground, overprint-offset rule (blue over pink, misregistered).
      return {
        ground: { background: c.background },
        frame: {
          border: `2px solid ${c.text}`,
          background: c.surface,
          boxShadow: `4px 4px 0 rgba(255, 72, 160, 0.45)`,
        },
        chip: {
          label: 'PRINT RUN',
          style: {
            fontFamily: '"Space Mono", monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: c.text,
            borderBottom: `3px solid ${c.primary}`,
            boxShadow: '0 3px 0 rgba(255, 72, 160, 0.6)',
          },
        },
      };
  }
};

interface SkinFrameProps {
  /** The slide's already-resolved theme (getSlideTheme output). */
  theme: Theme;
  /** The slide's data — decides skinnability via the world-grammar guard. */
  slideData: SlideData;
  /**
   * The slide's own skin field (`slide.themeOverride`) — the ONLY activation
   * signal. Only the flagged picker writes it, so with `slide_skins` off no
   * slide can carry one and every existing course renders unchanged.
   */
  slideThemeOverride?: ThemeId;
  children: React.ReactNode;
}

export function SkinFrame({ theme, slideData, slideThemeOverride, children }: SkinFrameProps) {
  const world = slideThemeOverride;
  // No per-slide skin, the override lost resolution (brand/group won), or the
  // world's grammar refuses this slide → render children untouched.
  if (
    !world ||
    !isThemeWorld(world) ||
    theme.id !== world ||
    !canWorldDressSlide(world, slideData)
  ) {
    return <>{children}</>;
  }
  const chrome = worldChrome(world);
  return (
    <div
      data-skin-world={world}
      style={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 'clamp(0.75rem, 2cqi, 1.5rem)',
        boxSizing: 'border-box',
        ...chrome.ground,
      }}
    >
      {chrome.overlay && (
        <div
          data-skin-overlay
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 2,
            ...chrome.overlay,
          }}
        />
      )}
      <div style={{ flexShrink: 0, display: 'flex', marginBottom: 10 }}>
        <span
          data-skin-chip
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            fontSize: 11,
            ...chrome.chip.style,
          }}
        >
          {/* Obsidian's red pin — a period detail, decorative only. */}
          {world === 'obsidian' && (
            <span
              aria-hidden="true"
              style={{ width: 7, height: 7, borderRadius: '50%', background: '#c03b2d' }}
            />
          )}
          {chrome.chip.label}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative', ...chrome.frame }}>
        {children}
      </div>
    </div>
  );
}
